import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import { useRouter } from '@core/utils/utils'
import useCommon from '@/views/organization/useCommon'
import useCommonDeclaration from '../useCommonDeclaration'
import { getDeclarationConfig } from '../declarationConfig'

export default function useDeclarationList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { getItemsAggregate } = useLambda()
  const { handleError } = useCommon()
  const {
    collection, deactivateDocument, uploadNewVersion, validateDocument, rejectDocument,
    canValidateDocument, sendNewVersionEmail, sendValidatedEmail, sendRejectedEmail,
  } = useCommonDeclaration()
  const { route } = useRouter()
  // Reactive: the three modules share this component, so navigating between them
  // (e.g. Lobby -> Conflict of Interest) reuses the instance without re-running setup.
  const config = computed(() => getDeclarationConfig(route.value?.path))
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
  // TODO: userData.companies no existe (Login.vue nunca lo arma) — userCompanies siempre
  // queda [], así que el filtro de empresa de más abajo nunca se aplica. Código muerto,
  // documentado en src/docs/permisos-plan.md ("Pendiente de decisión de negocio"), no tocar
  // sin autorización explícita.
  const {
    role, client = null, companies: userCompanies = [],
  } = store.state?.userStore?.userData

  const documents = ref([])
  const perPage = ref(10)
  const totalDocuments = ref(0)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const sortBy = ref('fechaProximaRevision')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  // Filters
  const tipoDocumentoFilter = ref(null)
  const tematicaFilter = ref(null)
  const estadoFilter = ref(null)
  const showInactive = ref(false)

  const tableColumns = computed(() => [
    {
      key: 'actions', label: i18n.t('message.tableHeader.actions'), sortable: false, thClass: 'declaration-col-actions', tdClass: 'declaration-col-actions',
    },
    { key: 'nombre', label: i18n.t(config.value.mainFieldLabel), sortable: true },
    { key: 'tipoDocumento', label: i18n.t('declaration.tipoDocumento'), sortable: true },
    { key: 'tematica', label: i18n.t('declaration.tematica'), sortable: true },
    { key: 'responsableNombre', label: i18n.t('declaration.responsable'), sortable: true },
    { key: 'versionActual', label: i18n.t('declaration.versionActual'), sortable: true },
    { key: 'estado', label: i18n.t('declaration.estado'), sortable: true },
    { key: 'fechaRegistro', label: i18n.t('declaration.fechaRegistro'), sortable: true },
    { key: 'fechaProximaRevision', label: i18n.t('declaration.fechaProximaRevision'), sortable: true },
  ])

  const estadoOptions = computed(() => [
    { value: 'pendiente', title: i18n.t('declaration.estado_pendiente') },
    { value: 'validado', title: i18n.t('declaration.estado_validado') },
    { value: 'rechazado', title: i18n.t('declaration.estado_rechazado') },
    { value: 'vencido', title: i18n.t('declaration.estado_vencido') },
  ])

  const fetchDocuments = async () => {
    documents.value = []
    isLoading.value = true

    try {
      const matchStage = {
        client_id: oid(client.$oid),
        type: config.value.type,
        deleted: { $ne: true },
      }

      if (!showInactive.value) {
        matchStage.activo = true
      }

      if (tipoDocumentoFilter.value) {
        matchStage.tipoDocumento = tipoDocumentoFilter.value
      }

      if (tematicaFilter.value) {
        // Lobby stores free text; Conflict of Interest stores fixed values.
        matchStage.tematica = config.value.tematicaFreeText
          ? { $regex: tematicaFilter.value, $options: 'i' }
          : tematicaFilter.value
      }

      if (estadoFilter.value) {
        matchStage.estado = estadoFilter.value
      }

      if (searchQuery.value) {
        matchStage.nombre = { $regex: searchQuery.value, $options: 'i' }
      }

      // Client filtering for manager/supervisor roles
      // TODO: nunca se cumple — userCompanyIds siempre es [] porque userData.companies no
      // existe (ver TODO arriba). Código muerto en la práctica, no tocar sin autorización.
      if (role === 'manager' || role === 'supervisor') {
        const userCompanyIds = userCompanies.map(c => c.value || c._id || c)
        if (userCompanyIds.length) {
          matchStage.empresas = { $in: userCompanyIds.map(id => oid(id)) }
        }
      }

      const sortStage = {}
      sortStage[sortBy.value] = isSortDirDesc.value ? -1 : 1

      const pipeline = [
        { $match: matchStage },
        {
          $lookup: {
            from: 'worker', localField: 'responsable', foreignField: '_id', as: 'responsableData', pipeline: [{ $project: { name: 1 } }],
          },
        },
        {
          $lookup: {
            from: 'worker', localField: 'trabajador', foreignField: '_id', as: 'trabajadorData', pipeline: [{ $project: { name: 1 } }],
          },
        },
        {
          $addFields: {
            responsableNombre: { $arrayElemAt: ['$responsableData.name', 0] },
            // Para Lobby/Collaborator (mainFieldKind='worker'), usar el nombre del trabajador
            // Para Supplier (mainFieldKind='text'), usar el campo 'nombre' existente
            nombre: {
              $cond: [
                { $gt: [{ $size: '$trabajadorData' }, 0] },
                { $arrayElemAt: ['$trabajadorData.name', 0] },
                '$nombre',
              ],
            },
          },
        },
        { $sort: sortStage },
        {
          $facet: {
            paginatedResults: [
              { $skip: (currentPage.value - 1) * perPage.value },
              { $limit: perPage.value },
              { $addFields: { _id: { $toString: '$_id' } } },
            ],
            totalCount: [{ $count: 'count' }],
          },
        },
      ]

      const result = await getItemsAggregate({ collection, pipeline })
      const facetResult = result[0] || {}

      const items = facetResult.paginatedResults || []
      totalDocuments.value = facetResult.totalCount?.[0]?.count || 0

      items.forEach(doc => {
        if (doc.fechaRegistro) {
          const d = new Date(doc.fechaRegistro)
          doc.fechaRegistroFormatted = d.toLocaleDateString(`${default_language || 'es'}-CL`, { timeZone: 'UTC' })
        }
        if (doc.fechaProximaRevision) {
          const d = new Date(doc.fechaProximaRevision)
          doc.fechaProximaRevisionFormatted = d.toLocaleDateString(`${default_language || 'es'}-CL`, { timeZone: 'UTC' })

          const now = new Date()
          const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))
          doc.isProximoAVencer = diffDays <= 30 && diffDays >= 0
          doc.isVencido = diffDays < 0
        }
      })

      documents.value = items
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('declaration.error_list') })
    } finally {
      isLoading.value = false
    }
  }

  const handleDeactivate = async documentId => {
    try {
      await deactivateDocument(documentId)
      showSuccessMessage(i18n.t('declaration.deactivated_success'))
      fetchDocuments()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  // Mismas 3 acciones que View.vue (subir nueva versión / validar / rechazar), disponibles
  // también desde el listado — reutilizan las funciones de useCommonDeclaration.js (que ya
  // tienen su propio guard hasPermission interno) y el mismo envío de email; solo cambia que
  // operan sobre la fila clickeada (`item`) en vez del `document` fijo de la vista de detalle.
  // Devuelven true/false (éxito/error) para que la lista sepa si cerrar el modal —
  // mismo criterio que View.vue, que solo cierra su modal cuando la operación no lanzó error.
  const handleUploadNewVersion = async (item, file) => {
    // eslint-disable-next-line no-underscore-dangle
    const itemId = item._id
    try {
      const newVersion = await uploadNewVersion(itemId, file, item)
      showSuccessMessage(i18n.t('declaration.version_uploaded'))

      const responsableId = item.responsable?.$oid || item.responsable?.toString()
      if (responsableId) {
        sendNewVersionEmail(itemId, item, newVersion, responsableId)
      }

      fetchDocuments()
      return true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('declaration.error_upload_version'))
      return false
    }
  }

  const handleValidate = async (item, fechaProximaRevision) => {
    // eslint-disable-next-line no-underscore-dangle
    const itemId = item._id
    try {
      await validateDocument(itemId, fechaProximaRevision)
      showSuccessMessage(i18n.t('declaration.validated_success'))
      sendValidatedEmail(itemId, item)
      fetchDocuments()
      return true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('declaration.error_validate'))
      return false
    }
  }

  const handleReject = async (item, motivo, fechaProximaRevision) => {
    // eslint-disable-next-line no-underscore-dangle
    const itemId = item._id
    try {
      await rejectDocument(itemId, motivo, fechaProximaRevision)
      showSuccessMessage(i18n.t('declaration.rejected_success'))
      sendRejectedEmail(itemId, item, motivo)
      fetchDocuments()
      return true
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('declaration.error_reject'))
      return false
    }
  }

  watch([currentPage, perPage], () => {
    fetchDocuments()
  })

  watch([searchQuery, sortBy, isSortDirDesc, tipoDocumentoFilter, tematicaFilter, estadoFilter, showInactive], () => {
    currentPage.value = 1
    fetchDocuments()
  })

  // Navigating between the shared modules (Lobby / Conflict of Interest) reuses this
  // component instance; reset filters and reload for the new type.
  watch(() => route.value?.path, () => {
    currentPage.value = 1
    tipoDocumentoFilter.value = null
    tematicaFilter.value = null
    estadoFilter.value = null
    fetchDocuments()
  })

  return {
    config,
    fetchDocuments,
    documents,
    tableColumns,
    perPage,
    currentPage,
    totalDocuments,
    searchQuery,
    sortBy,
    isSortDirDesc,
    isLoading,
    handleDeactivate,
    handleUploadNewVersion,
    handleValidate,
    handleReject,
    canValidateDocument,
    tipoDocumentoFilter,
    tematicaFilter,
    estadoFilter,
    showInactive,
    estadoOptions,
    role,
  }
}
